import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { Divider } from "@/components/shared/divider";
import { useBookingState } from "@/store/booking/useBookingState";
import type { ClientServiceProfileFragmentFragment } from "@/__generated__/graphql";
import { ClientServiceProfileFragmentFragmentDoc } from "@/__generated__/graphql";
import { Button, View } from "@/components/ui";
import { useCreateClientServiceProfileMutation } from "@/gqlhooks/profile/useCreateClientServiceProfileMutation";
import { ServiceProfileCard } from "@/components/shared/service-profile/service-profile-card";
import { useClientServiceProfileByFiltersQuery } from "@/gqlhooks/profile/useClientServiceProfileByFiltersQuery";
import { CreateNewProfileCard } from "@/components/shared/service-profile/create-new-profile-card";
import { useState } from "react";
import { useUpdateClientServiceProfileMutation } from "@/gqlhooks/profile/useUpdateClientServiceProfileMutation";
import {
  transformServiceProfile,
  transformServiceProfileToDetail,
} from "@/containers/profile/service-profile/lib";
import { LoadingDialog } from "@/components/shared/loading-dialog";
import { ClientInstance } from "@/lib/graphql/apollo";
import { Typography } from "@/components/shared/typography";
import { ListHeading } from "@/components/shared/list-header";
import { ClipboardText } from "@/components/shared/icons";
import { showToast } from "@/components/ui/toast/show-toast";
import { getFragmentData } from "@/__generated__";
import { FormProvider } from "react-hook-form";
import type {
  ElderCareFormData} from "@/containers/profile/service-profile/service-profile-form/elder/useElderCareForm";
import {
  useElderCareForm,
} from "@/containers/profile/service-profile/service-profile-form/elder/useElderCareForm";
import { ElderCareForm } from "@/containers/profile/service-profile/service-profile-form/elder/form";

type ElderProfileDetail = ElderCareFormData & { id: string };

export const setElderProfileInfo = (data: ElderProfileDetail) => {
  useBookingState.getState().setBookingState({
    elderCareProfileInfo: {
      ...data,
    },
    selectedServiceProfileId: data.id,
  });
};

export const ElderProfileModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const [isCreating, setIsCreatingNew] = useState(false);
  const [editingProfile, setEditingProfile] =
    useState<ElderProfileDetail | null>(null);

  const handleClose = (closeAll = false) => {
    setIsCreatingNew(false);
    setEditingProfile(null);
    if (closeAll) {
      setOpen(false);
    }
  };

  const handleCreate = () => {
    setIsCreatingNew(true);
    setEditingProfile(null);
  };

  const handleEdit = (profile: ElderProfileDetail) => {
    setEditingProfile(profile);
    setIsCreatingNew(false);
  };

  if (!open) {
    return null;
  }

  if (isCreating || editingProfile) {
    return (
      <FormElderProfileModal
        key={editingProfile?.id}
        editingProfile={editingProfile}
        handleClose={handleClose}
      />
    );
  }

  return (
    <ListElderProfileModal
      handleCreate={handleCreate}
      handleEdit={handleEdit}
      handleClose={() => handleClose(true)}
    />
  );
};

export const FormElderProfileModal = ({
  handleClose,
  editingProfile,
}: {
  handleClose: (closeAll?: boolean) => void;
  editingProfile: ElderProfileDetail | null;
}) => {
  const elderCareForm = useElderCareForm(editingProfile ?? undefined);

  const { control, handleSubmit } = elderCareForm;

  const { createProfile, loading } = useCreateClientServiceProfileMutation();
  const [updateProfile, { loading: updateProfileLoading }] =
    useUpdateClientServiceProfileMutation();

  const onSave = async (data: ElderCareFormData) => {
    const { name, ...profileAttributes } = data;

    let selectedServiceProfile: ClientServiceProfileFragmentFragment | null =
      null;
    const transformedProfileAttributes = transformServiceProfile(
      "elder",
      profileAttributes,
    );
    if (editingProfile) {
      const res = await updateProfile({
        variables: {
          input: {
            id: editingProfile.id,
            name,
            profileAttributes: transformedProfileAttributes,
          },
        },
      });

      if (res.data?.updateClientServiceProfile) {
        selectedServiceProfile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          res.data?.updateClientServiceProfile.clientServiceProfile,
        );
        showToast({
          title: "Profile updated successfully",
          type: "success",
        });
      }
    } else {
      const res = await createProfile({
        type: "elder",
        name: data.name,
        profileAttributes: transformedProfileAttributes,
        primary: true,
      });

      if (res.data?.createClientServiceProfile) {
        selectedServiceProfile = getFragmentData(
          ClientServiceProfileFragmentFragmentDoc,
          res.data?.createClientServiceProfile.clientServiceProfile,
        );
        showToast({
          title: "Profile created successfully",
          type: "success",
        });
      }
    }

    if (selectedServiceProfile) {
      setElderProfileInfo({
        id: selectedServiceProfile.id,
        ...transformServiceProfileToDetail("elder", selectedServiceProfile),
      });
    }
    handleClose(true);
  };

  return (
    <BottomDrawerModal open onOpenChange={handleClose} scroll>
      <FormProvider {...elderCareForm}>
        <ElderCareForm control={control} />
      </FormProvider>

      <Divider className="my-2" />
      <Button variant="primary" color="CTA" onClick={handleSubmit(onSave)}>
        Save
      </Button>
      <LoadingDialog open={loading || updateProfileLoading} />
    </BottomDrawerModal>
  );
};

export const ListElderProfileModal = ({
  handleCreate,
  handleEdit,
  handleClose,
}: {
  handleCreate: () => void;
  handleEdit: (profile: ElderProfileDetail) => void;
  handleClose: () => void;
}) => {
  const { profiles } = useClientServiceProfileByFiltersQuery("elder");
  const selectedServiceProfileId = useBookingState(
    ({ data }) => data.selectedServiceProfileId,
  );

  const _handleEdit = (profileId: string) => {
    const profile = ClientInstance.readFragment({
      id: `ClientServiceProfile:${profileId}`,
      fragment: ClientServiceProfileFragmentFragmentDoc,
    });
    if (profile) {
      handleEdit({
        id: profile.id,
        ...transformServiceProfileToDetail("elder", profile),
      });
    }
  };

  const _setElderProfileInfo = (profileId: string) => {
    const profile = ClientInstance.readFragment({
      id: `ClientServiceProfile:${profileId}`,
      fragment: ClientServiceProfileFragmentFragmentDoc,
    });
    if (profile) {
      setElderProfileInfo({
        id: profileId,
        ...transformServiceProfileToDetail("elder", profile),
      });
    }
    handleClose();
  };

  return (
    <BottomDrawerModal open onOpenChange={handleClose}>
      <ListHeading title="Choose an Elder Profile" icon={ClipboardText} />
      <Typography
        color="foreground-intermediate"
        variant="body-md"
        className="pt-1"
      >
        Select from your saved profiles or add a new one.
      </Typography>
      <Divider className="my-2" />
      <View className="flex flex-col gap-3">
        {profiles.map((profile) => (
          <ServiceProfileCard
            key={profile.id}
            profile={profile}
            selected={profile.id === selectedServiceProfileId}
            onClick={() => _setElderProfileInfo(profile.id)}
            handleEdit={() => _handleEdit(profile.id)}
          />
        ))}
        {profiles.length < 3 && (
          <CreateNewProfileCard onClick={() => handleCreate()} />
        )}
      </View>
    </BottomDrawerModal>
  );
};
